import { Box, Button, Grid, IconButton, Typography } from "@mui/material";
import { useContext, useEffect, useMemo, useState } from "react";
import { Plus } from "react-feather";
import { PublicTransportForm } from "../../components/moduleForms/PublicTransport/PublicTransportForm";
import { PublicTransportAccountSummary } from "../../components/PublicTransportAccountSummary";
import { ColouredContainer } from "../../components/shared/ColoredContainer";
import { LoadingSpinner } from "../../components/shared/LoadingSpinner";
import { PageBlurb } from "../../components/shared/PageBlurb";
import { PageHeader } from "../../components/shared/PageHeader";
import { SideDrawer } from "../../components/shared/SideDrawer";
import { SalarySacrificesTable } from "../../components/table/SalarySacrifices/SalarySacrificesTable";
import { SalarySacrificesTableControlBar } from "../../components/table/SalarySacrifices/SalarySacrificesTableControlBar";
import { PageHeaderControlContext } from "../../context/PageHeaderControlContext";
import { useDeletePlan } from "../../hooks/plans/useDeletePlan";
import { EmployerModuleType } from "../../models/accounts/employerModuleType";
import { Plan } from "../../models/accounts/plan";
import {
  SalarySacrifice,
  SalarySacrificeStatus
} from "../../models/salarySacrifices/salarySacrifice";
import { isLoading } from "../../utils/isLoading";
import { extraordinaryColors } from "../../utils/theme";
import { useCustomQuery, useData } from "../../utils/useData";
import {
  DeletePlanButton,
  PlanStatistics,
  StyledEditIcon
} from "../allowances/Allowances";

const PublicTransport = () => {
  const { plans, serviceSectors, accounts, employees, employer } = useData();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [selectedStatuses, setSelectedStatuses] = useState<
    SalarySacrificeStatus[]
  >([]);

  const { setHeaderActions } = useContext(PageHeaderControlContext);
  const [isAdding, setIsAdding] = useState<boolean>(false);
  const [editingPlan, setEditingPlan] = useState<Plan | undefined>(undefined);
  const [isEditing, setIsEditing] = useState<boolean>(false);

  const mutationDeletePlan = useDeletePlan({
    successMessage: "Deleted Successfully"
  });

  const queryString = [
    `page=${page}`,
    `pageSize=${pageSize}`,
    ...selectedStatuses.map((status) => `status=${status}`)
  ]
    .filter(Boolean)
    .join("&");

  const salarySacrifices = useCustomQuery<{
    items: SalarySacrifice[];
    totalItems: number;
  }>({
    endpoint: `/employers/${employer.data?.id}/salary-sacrifices?${queryString}`,
    options: {
      enabled: !!employer.data?.id
    }
  });

  useEffect(() => {
    salarySacrifices.refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, selectedStatuses]);

  const handleStatusesChange = (statuses: SalarySacrificeStatus[]) => {
    setSelectedStatuses(statuses);
    setPage(0);
  };

  const publicTransportPlans: Plan[] = useMemo(() => {
    return (
      plans.data?.items.filter(
        (plan) =>
          plan.employerModuleOptions.employerModuleType ===
          EmployerModuleType.PUBLIC_TRANSPORT
      ) ?? []
    );
  }, [plans.data]);

  const planTotalUsersMap: Record<string, number> = useMemo(() => {
    const planMap: Record<string, number> = {};
    accounts.data?.items.forEach((account) => {
      planMap[account.planId] ??= 0;
      planMap[account.planId] += 1;
    });

    return planMap;
  }, [accounts.data]);

  useEffect(() => {
    setHeaderActions([
      <Button
        variant="contained"
        onClick={() => setIsAdding(true)}
        startIcon={<Plus size={16} />}
        size="medium"
        disabled={false}
      >
        New
      </Button>
    ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isLoading([plans, employees, accounts, serviceSectors]))
    return <LoadingSpinner />;

  const renderPlan = (plan: Plan) => {
    return (
      <Grid item xs={12} md={6} key={plan.id}>
        <ColouredContainer
          title={plan.name}
          color={extraordinaryColors.purple.main}
          toolbarItems={
            <>
              <DeletePlanButton
                totalUsers={planTotalUsersMap[plan.id] ?? 0}
                onClick={() => mutationDeletePlan.mutate(plan)}
              />
              <IconButton
                onClick={() => {
                  setIsEditing(true);
                  setEditingPlan(plan);
                }}
              >
                <StyledEditIcon />
              </IconButton>
            </>
          }
          subtitleItems={
            <PlanStatistics totalUsers={planTotalUsersMap[plan.id] ?? 0} />
          }
        >
          <Box sx={{ padding: 2 }}>
            <PublicTransportAccountSummary
              employerAlphanumericId={employer.data?.alphanumericId ?? ""}
              planInfo={{
                ...plan,
                planType: plan.type,
                serviceSectorIds:
                  plan.controls.serviceSectors?.map((ssref) => ssref.id) ?? []
              }}
            />
          </Box>
        </ColouredContainer>
      </Grid>
    );
  };

  return (
    <>
      <PageHeader pageTitle="Public Transport" />
      <PageBlurb
        description="Let employees pay for public transport using pre-tax income, saving them $768+ a year. You choose the contribution type (salary sacrifice or employer-paid), and the platform takes care of tax treatment, tracking, and reporting. Fully IRD-compliant and easy to roll out."
        borderColor={extraordinaryColors.purple.main}
      />

      <Grid container spacing={2}>
        {publicTransportPlans
          .filter(
            (p) =>
              p.controls.purchases.isEnabled &&
              !p.controls.payments.isEnabled &&
              p.employerModuleOptions.employerModuleType ===
                EmployerModuleType.PUBLIC_TRANSPORT
          )
          .map(renderPlan)}
      </Grid>

      <Grid container ml={0} mt={3}>
        <Typography variant="h6">Contributions</Typography>
        <SalarySacrificesTableControlBar
          selectedStatuses={selectedStatuses}
          onStatusesChange={handleStatusesChange}
        />
        <SalarySacrificesTable
          salarySacrifices={salarySacrifices.data?.items ?? []}
          totalItems={salarySacrifices.data?.totalItems ?? 0}
          page={page}
          pageSize={pageSize}
          updatePage={setPage}
          updatePageSize={setPageSize}
          isLoading={salarySacrifices.isLoading}
          refetch={salarySacrifices.refetch}
        />
      </Grid>

      <SideDrawer title="Add" open={isAdding} setIsOpen={setIsAdding}>
        <PublicTransportForm onClose={() => setIsAdding(false)} />
      </SideDrawer>
      <SideDrawer title="Edit" open={isEditing} setIsOpen={setIsEditing}>
        <PublicTransportForm
          plan={editingPlan!}
          onClose={() => setIsEditing(false)}
        />
      </SideDrawer>
    </>
  );
};

export { PublicTransport };
